import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, Clock, Users, DollarSign, CheckCircle2, AlertCircle, UserCog, Pencil, Check, X } from 'lucide-react';
import { formatCurrency } from '@/utils';
import { getAvatarColor } from '@/lib/utils';
import { CaseMilestone } from '@/hooks/useCaseMilestones';
import CaseStageSidebar from '@/components/cases/stages/CaseStageSidebar';
import { format, parseISO, isAfter, isBefore, addDays } from 'date-fns';

interface TeamMember {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  display_name?: string | null;
}

interface CaseDetailSidebarProps {
  caseId?: string | null;
  caseData: any;
  deal: any;
  paymentStatus: any;
  salesConsultant: TeamMember | null;
  customerExperience: TeamMember | null;
  caseAnalyst: TeamMember | null;
  milestones: CaseMilestone[];
  navigate: (path: string) => void;
  onAssignConsultant?: () => void;
  onAssignCX?: () => void;
  onAssignAnalyst?: () => void;
  onUpdateCase?: (updates: Record<string, any>) => Promise<any>;
}

function getUserName(user: TeamMember | null): string {
  if (!user) return 'Não atribuído';
  if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
  return user.display_name || user.full_name || 'Sem nome';
}

function formatDateBR(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'dd/MM/yyyy');
  } catch {
    return iso;
  }
}

type EditableField = 'sponsor_company' | 'sponsor_type';

export default function CaseDetailSidebar({
  caseId,
  caseData,
  deal,
  paymentStatus,
  salesConsultant,
  customerExperience,
  caseAnalyst,
  milestones,
  navigate,
  onAssignConsultant,
  onAssignCX,
  onAssignAnalyst,
  onUpdateCase,
}: CaseDetailSidebarProps) {
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Calculate days open
  const daysOpen = caseData?.created_at
    ? Math.floor((Date.now() - new Date(caseData.created_at).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  const catalog = caseData?.item?.catalog;
  const variant = caseData?.item?.variant_label || caseData?.item?.catalog_variant;

  // Extract PWD milestone data
  const pwdMilestone = milestones.find(
    (m) => m.name === 'PWD (Salário Prevalecente)' || m.name?.toLowerCase().startsWith('pwd')
  );
  const pwdCaseNumber = pwdMilestone?.data?.case_number as string | null | undefined;
  const pwdWageLevel = pwdMilestone?.data?.wage_level as string | null | undefined;
  const pwdSocCode = pwdMilestone?.data?.soc_code as string | null | undefined;
  const pwdValidityDate = pwdMilestone?.data?.validity_date as string | null | undefined;

  // Validity badge color
  const pwdValidityStatus = (() => {
    if (!pwdValidityDate) return null;
    try {
      const d = parseISO(pwdValidityDate);
      const today = new Date();
      if (isBefore(d, today)) return 'expired';
      if (isBefore(d, addDays(today, 60))) return 'expiring';
      return 'valid';
    } catch {
      return null;
    }
  })();

  const startEdit = (field: EditableField) => {
    setEditingField(field);
    setEditValue(caseData?.[field] ?? '');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!onUpdateCase || !editingField) return;
    setIsSaving(true);
    try {
      await onUpdateCase({ [editingField]: editValue || null });
    } finally {
      setIsSaving(false);
      setEditingField(null);
    }
  };

  const sponsorCompany: string | null = caseData?.sponsor_company ?? null;
  const sponsorType: string | null = caseData?.sponsor_type ?? null;

  const hasPwdData = pwdCaseNumber || pwdWageLevel || pwdSocCode || pwdValidityDate;
  const isSponsorCase = catalog?.name?.toLowerCase().includes('perm') ||
    catalog?.name?.toLowerCase().includes('eb-2') ||
    catalog?.name?.toLowerCase().includes('eb2') ||
    catalog?.name?.toLowerCase().includes('eb-3') ||
    catalog?.name?.toLowerCase().includes('eb3') ||
    sponsorCompany ||
    hasPwdData;

  return (
    <div className="space-y-4">
      {/* 1. Detalhes do Processo */}
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
        <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Detalhes do Processo
        </h3>
        <div className="space-y-2.5">
          <div className="flex justify-between items-center">
            <span className="text-[11px] text-muted-foreground">Tipo</span>
            <span className="text-[11px] font-medium text-foreground">{catalog?.name || caseData?.visa_type || '—'}</span>
          </div>
          {variant && (
            <div className="flex justify-between items-center">
              <span className="text-[11px] text-muted-foreground">Variante</span>
              <span className="text-[11px] font-medium text-foreground">{variant}</span>
            </div>
          )}
          {caseData?.filing_target && (
            <div className="flex justify-between items-center">
              <span className="text-[11px] text-muted-foreground">Filing Target</span>
              <span className="text-[11px] font-medium text-foreground">{caseData.filing_target}</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-[11px] text-muted-foreground">Dias Aberto</span>
            <span className="text-[11px] font-mono font-medium text-foreground">{daysOpen} dias</span>
          </div>
        </div>
      </div>

      {/* 2. Sponsor (PERM/EB cases) */}
      {(isSponsorCase || onUpdateCase) && (
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Empregador / Sponsor
          </h3>
          <div className="space-y-2.5">
            {/* Empresa Sponsor */}
            <div className="flex justify-between items-start gap-2">
              <span className="text-[11px] text-muted-foreground flex-shrink-0">Empresa</span>
              {editingField === 'sponsor_company' ? (
                <div className="flex items-center gap-1 flex-1 justify-end">
                  <input
                    autoFocus
                    className="text-[11px] border border-border rounded px-1.5 py-0.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-full max-w-[140px]"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                  />
                  <button onClick={saveEdit} disabled={isSaving} className="p-0.5 hover:text-emerald-600">
                    <Check className="w-3 h-3" />
                  </button>
                  <button onClick={cancelEdit} className="p-0.5 hover:text-rose-500">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span className={`text-[11px] font-medium ${sponsorCompany ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {sponsorCompany || '—'}
                  </span>
                  {onUpdateCase && (
                    <button onClick={() => startEdit('sponsor_company')} className="p-0.5 hover:bg-accent rounded transition-colors">
                      <Pencil className="w-2.5 h-2.5 text-muted-foreground" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Tipo de Sponsor */}
            <div className="flex justify-between items-center gap-2">
              <span className="text-[11px] text-muted-foreground flex-shrink-0">Tipo</span>
              {editingField === 'sponsor_type' ? (
                <div className="flex items-center gap-1">
                  <select
                    autoFocus
                    className="text-[11px] border border-border rounded px-1.5 py-0.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                  >
                    <option value="">—</option>
                    <option value="interno">Interno</option>
                    <option value="externo">Externo</option>
                  </select>
                  <button onClick={saveEdit} disabled={isSaving} className="p-0.5 hover:text-emerald-600">
                    <Check className="w-3 h-3" />
                  </button>
                  <button onClick={cancelEdit} className="p-0.5 hover:text-rose-500">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  {sponsorType ? (
                    <Badge className={`text-[10px] px-2 py-0 h-4 border-0 capitalize ${
                      sponsorType === 'interno'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    }`}>
                      {sponsorType === 'interno' ? 'Interno' : 'Externo'}
                    </Badge>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">—</span>
                  )}
                  {onUpdateCase && (
                    <button onClick={() => startEdit('sponsor_type')} className="p-0.5 hover:bg-accent rounded transition-colors">
                      <Pencil className="w-2.5 h-2.5 text-muted-foreground" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* PWD data (read-only from milestone) */}
            {hasPwdData && (
              <>
                <div className="mt-2 pt-2 border-t border-border space-y-1.5">
                  <div className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground">PWD</div>
                  {pwdCaseNumber && (
                    <div className="flex justify-between items-center">
                      <span className="text-[11px] text-muted-foreground">Nº Caso PWD</span>
                      <span className="text-[11px] font-mono font-medium text-foreground">{pwdCaseNumber}</span>
                    </div>
                  )}
                  {pwdSocCode && (
                    <div className="flex justify-between items-center">
                      <span className="text-[11px] text-muted-foreground">Cód. SOC</span>
                      <span className="text-[11px] font-mono font-medium text-foreground">{pwdSocCode}</span>
                    </div>
                  )}
                  {pwdWageLevel && (
                    <div className="flex justify-between items-center">
                      <span className="text-[11px] text-muted-foreground">Nível Salarial</span>
                      <span className="text-[11px] font-medium text-foreground">{pwdWageLevel}</span>
                    </div>
                  )}
                  {pwdValidityDate && (
                    <div className="flex justify-between items-center">
                      <span className="text-[11px] text-muted-foreground">Validade</span>
                      <span className={`text-[11px] font-medium ${
                        pwdValidityStatus === 'expired'
                          ? 'text-rose-600 dark:text-rose-400'
                          : pwdValidityStatus === 'expiring'
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-foreground'
                      }`}>
                        {formatDateBR(pwdValidityDate)}
                        {pwdValidityStatus === 'expired' && ' · Vencida'}
                        {pwdValidityStatus === 'expiring' && ' · A vencer'}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 3. Negociação */}
      {deal && (
        <div className="bg-card border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-[10px] font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider">
                Negociação
              </h3>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/dealdetail?id=${deal.id}`)}
              className="text-[10px] h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30"
            >
              Ver Detalhes →
            </Button>
          </div>
          <div className="text-lg font-bold text-blue-700 dark:text-blue-300 font-mono">
            {formatCurrency(deal.total_amount)}
          </div>
        </div>
      )}

      {/* 4. Equipe Responsável */}
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
        <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Equipe Responsável
        </h3>
        <div className="space-y-2.5">
          {[
            { label: 'Consultor', user: salesConsultant, onAssign: onAssignConsultant },
            { label: 'CX', user: customerExperience, onAssign: onAssignCX },
            { label: 'Analista', user: caseAnalyst, onAssign: onAssignAnalyst },
          ].map(({ label, user, onAssign }) => (
            <div key={label} className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0 ${
                user ? `bg-gradient-to-br ${getAvatarColor(user.first_name || '')}` : 'bg-muted'
              }`}>
                {user ? (user.first_name?.charAt(0)?.toUpperCase() || '?') : '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] text-muted-foreground uppercase tracking-wide">{label}</div>
                <div className={`text-[11px] font-medium truncate ${user ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {getUserName(user)}
                </div>
              </div>
              {onAssign && (
                <button onClick={onAssign} className="p-0.5 hover:bg-accent rounded transition-colors flex-shrink-0" title={`Atribuir ${label}`}>
                  <UserCog className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 5. Próximas Ações (from CaseStageSidebar) */}
      {milestones.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <CaseStageSidebar milestones={milestones} />
        </div>
      )}

      {/* 6. Financeiro */}
      {deal && (
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Financeiro
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-[11px] text-muted-foreground">Valor Total</span>
              <span className="text-[11px] font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(deal.total_amount)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[11px] text-muted-foreground">Pago</span>
              <span className="text-[11px] font-mono font-medium text-foreground">
                {formatCurrency((deal.total_amount || 0) - (paymentStatus?.totalPending || 0))}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[11px] text-muted-foreground">Restante</span>
              <span className="text-[11px] font-mono font-semibold text-amber-600 dark:text-amber-400">
                {formatCurrency(paymentStatus?.totalPending || 0)}
              </span>
            </div>
            {salesConsultant && (
              <div className="flex justify-between items-center pt-1 border-t border-border">
                <span className="text-[11px] text-muted-foreground">Vendedor</span>
                <span className="text-[11px] font-medium text-foreground">{getUserName(salesConsultant)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
